/**
 * Reports Index Page
 *
 * Dashboard for viewing financial reports with multiple visualizations.
 * Features tab navigation between report types, date range filtering,
 * and granularity selection.
 *
 * Features:
 * - Tab navigation (Spending, Income vs Expenses, Account Balances)
 * - Date range picker with presets
 * - Granularity toggle for time-series reports
 * - Loading skeleton during data fetch
 * - Error handling with toast notifications
 */

import { useEffect, useState } from 'react';
import { Head, Link } from '@inertiajs/react';
import { Toaster, toast } from 'sonner';
import { PieChart, BarChart3, TrendingUp, ArrowLeft, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { Navbar } from '@/components';

import { useSpendingReport } from '@/hooks/useSpendingReport';
import { useIncomeExpensesReport } from '@/hooks/useIncomeExpensesReport';
import { useAccountBalancesReport } from '@/hooks/useAccountBalancesReport';
import { ReportFilters } from './components/ReportFilters';
import { SpendingByCategoryChart } from './components/SpendingByCategoryChart';
import { IncomeVsExpensesChart } from './components/IncomeVsExpensesChart';
import { AccountBalancesChart } from './components/AccountBalancesChart';
import type { ReportGranularity } from '@/types/accounting';

/**
 * Report tab types
 */
type ReportTab = 'spending' | 'income-expenses' | 'balances';

/**
 * Tab configuration
 */
const TABS: { id: ReportTab; label: string; icon: React.ElementType }[] = [
  { id: 'spending', label: 'Spending by Category', icon: PieChart },
  { id: 'income-expenses', label: 'Income vs Expenses', icon: BarChart3 },
  { id: 'balances', label: 'Account Balances', icon: TrendingUp },
];

/**
 * Get default date range (current year)
 */
function getDefaultDateRange() {
  const now = new Date();
  const year = now.getFullYear();
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

/**
 * ReportsIndex is the main page for viewing financial reports.
 */
export default function ReportsIndex() {
  const [activeTab, setActiveTab] = useState<ReportTab>('spending');
  const [granularity, setGranularity] = useState<ReportGranularity>('monthly');
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Hooks for fetching report data
  const spendingReport = useSpendingReport(dateRange.from, dateRange.to);
  const incomeExpensesReport = useIncomeExpensesReport(
    dateRange.from,
    dateRange.to,
    granularity
  );
  const accountBalancesReport = useAccountBalancesReport(
    dateRange.from,
    dateRange.to,
    granularity
  );

  // Get current report state based on active tab
  const getCurrentReport = () => {
    switch (activeTab) {
      case 'spending':
        return spendingReport;
      case 'income-expenses':
        return incomeExpensesReport;
      case 'balances':
        return accountBalancesReport;
    }
  };

  const currentReport = getCurrentReport();

  // Fetch report on mount and when filters change
  useEffect(() => {
    const report = getCurrentReport();
    report.fetchReport().catch(() => {
      // Error is handled by the hook
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange.from, dateRange.to, granularity]);

  // Show error toast on error
  useEffect(() => {
    if (currentReport.error) {
      toast.error(currentReport.error);
    }
  }, [currentReport.error]);

  return (
    <>
      <Head title="Reports" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="reports-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <Link
                href="/accounting/transactions"
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={16} />
                Transactions
              </Link>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="text-blue-500" size={24} />
                  Reports
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Visualize your financial data
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6">
            <nav className="flex gap-1" role="tablist">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={clsx(
                      'flex items-center gap-2 px-4 py-3 text-sm font-medium',
                      'border-b-2 transition-colors',
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <ReportFilters
              granularity={granularity}
              onGranularityChange={setGranularity}
              showGranularity={activeTab !== 'spending'}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </div>
        </div>

        {/* Main Content */}
        <main className="px-6 py-6 max-w-7xl mx-auto">
          {/* Error State */}
          {currentReport.error && !currentReport.loading && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-red-800">Error Loading Report</h3>
                <p className="text-sm text-red-700 mt-1">{currentReport.error}</p>
                <button
                  onClick={() => currentReport.fetchReport()}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {currentReport.loading && !currentReport.data && (
            <div className="space-y-6">
              {/* Chart Skeleton */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-48 mb-6" />
                <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading report data...
                  </div>
                </div>
              </div>

              {/* Summary Cards Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse"
                  >
                    <div className="h-3 bg-gray-200 rounded w-24 mb-2" />
                    <div className="h-8 bg-gray-200 rounded w-32" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading indicator for refresh */}
          {currentReport.loading && currentReport.data && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" />
              Refreshing...
            </div>
          )}

          {/* Report Content - Placeholder Charts */}
          {currentReport.data && (
            <>
              {activeTab === 'spending' && (
                <SpendingByCategoryChart
                  data={spendingReport.data}
                  loading={spendingReport.loading}
                />
              )}

              {activeTab === 'income-expenses' && (
                <IncomeVsExpensesChart
                  data={incomeExpensesReport.data}
                  loading={incomeExpensesReport.loading}
                />
              )}

              {activeTab === 'balances' && (
                <AccountBalancesChart
                  data={accountBalancesReport.data}
                  loading={accountBalancesReport.loading}
                />
              )}
            </>
          )}

          {/* Empty State - No Data */}
          {!currentReport.loading && currentReport.data && (
            <>
              {activeTab === 'income-expenses' &&
                incomeExpensesReport.data?.periods.length === 0 && (
                  <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-6 py-12 text-center">
                      <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h2 className="text-lg font-semibold text-gray-700 mb-2">
                        No Income/Expense Data
                      </h2>
                      <p className="text-sm text-gray-500 max-w-md mx-auto">
                        No transactions found for the selected date range.
                        Try adjusting your filters or importing some transactions.
                      </p>
                    </div>
                  </div>
                )}

              {activeTab === 'balances' &&
                accountBalancesReport.data?.accounts.length === 0 && (
                  <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-6 py-12 text-center">
                      <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h2 className="text-lg font-semibold text-gray-700 mb-2">
                        No Account Balances
                      </h2>
                      <p className="text-sm text-gray-500 max-w-md mx-auto">
                        No account balance data found for the selected date range.
                        Try adjusting your filters.
                      </p>
                    </div>
                  </div>
                )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
